'use client';

import { Button } from '@app/app/_components/Button/Button';
import { Countdown } from '@app/app/_components/Countdown/Countdown';
import { Input } from '@app/app/_components/Input/Input';
import { Message } from '@app/app/_components/Message/Message';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { QuestionCircleIcon } from '@app/app/_icons/QuestionCircleIcon';
import { SpinnerIcon } from '@app/app/_icons/SpinnerIcon';
import { FormStatus } from '@app/lib/form/contracts/FormStatus';
import { useChangeEffect } from '@app/lib/use-change-effect/useChangeEffect';
import { getMobileIdStatus } from '@app/service/auth/actions/getMobileIdStatus';
import { loginWithMobileId } from '@app/service/auth/actions/loginWithMobileId';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormState } from 'react-dom';

interface Props {
    onSuccess?: () => void;
    disableAutoFill?: boolean;
    disablePasting?: boolean;
}

export function MobileIdLoginSection({ onSuccess, disableAutoFill = false, disablePasting = false }: Props) {
    const checkTimeoutHandle = useRef<NodeJS.Timeout>();

    const appParameters = useAppParameters();

    const t = useTranslations();

    const autoComplete = disableAutoFill ? 'off' : undefined;

    const [waiting, setWaiting] = useState(false);
    const [token, setToken] = useState<string | null>(null);
    const [controlCode, setControlCode] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const [formState, formAction] = useFormState(loginWithMobileId, { status: FormStatus.Pending });

    const handleReset = useCallback(() => {
        setToken(null);
        setControlCode(null);
        setWaiting(false);
    }, []);

    const handleCancel = useCallback(
        (message: string | null) => {
            setErrorMessage(message);

            handleReset();
        },
        [handleReset],
    );

    const handleTimeElapsed = useCallback(() => {
        handleCancel('auth.mobileIdTimeElapsed');
    }, [handleCancel]);

    const handleCheck = useCallback(async () => {
        if (token == null) {
            return handleCancel('auth.mobileIdError');
        }

        const result = await getMobileIdStatus(token);

        if (result === FormStatus.Pending) {
            checkTimeoutHandle.current = setTimeout(handleCheck, appParameters.mobileId.checkInterval);

            return;
        }

        if (result === FormStatus.Success) {
            return onSuccess?.();
        }

        if (result === FormStatus.AdminRestrictedError) {
            handleCancel('auth.adminRestrictionError');

            return;
        }

        handleCancel('auth.mobileIdError');
    }, [appParameters, handleCancel, onSuccess, token]);

    const handleCancelClick = useCallback(() => {
        handleCancel(null);
    }, [handleCancel]);

    useEffect(() => {
        setErrorMessage(null);

        if (formState.status === FormStatus.Success) {
            setToken(formState.data?.token ?? null);
            setControlCode(formState.data?.controlCode ?? null);
            setWaiting(true);
        }
    }, [formState]);

    useChangeEffect(() => {
        if (waiting) {
            setErrorMessage(null);

            checkTimeoutHandle.current = setTimeout(handleCheck, appParameters.mobileId.checkInterval);

            return;
        }

        if (checkTimeoutHandle.current != null) {
            clearTimeout(checkTimeoutHandle.current);
        }
    }, waiting);

    useEffect(() => {
        return () => {
            if (checkTimeoutHandle.current != null) {
                clearInterval(checkTimeoutHandle.current);
            }
        };
    }, []);

    return (
        <>
            {!waiting && (
                <form action={formAction} className="flex flex-col gap-8">
                    <div className="flex flex-col gap-6">
                        {formState.status === FormStatus.Error && <Message>{t(formState.message)}</Message>}
                        {errorMessage != null && <Message>{t(errorMessage)}</Message>}

                        <Input
                            noSpaces
                            name="phoneNumber"
                            label={t('auth.phoneNumberLabel')}
                            placeholder={t('auth.phoneNumberPlaceholder')}
                            labelAddon={<QuestionCircleIcon className="size-4" />}
                            autoComplete={autoComplete}
                            disabledPasting={disablePasting}
                            error={formState.violations?.phoneNumber}
                        />

                        <Input
                            noSpaces
                            name="idCode"
                            label={t('auth.idCodeLabel')}
                            placeholder={t('auth.idCodePlaceholder')}
                            labelAddon={<QuestionCircleIcon className="size-4" />}
                            autoComplete={autoComplete}
                            disabledPasting={disablePasting}
                            error={formState.violations?.idCode}
                        />
                    </div>

                    <Button>{t('auth.login')}</Button>
                </form>
            )}

            {waiting && (
                <div className="flex flex-col gap-8">
                    <div className="flex flex-col items-center gap-1">
                        <div className="text-center">
                            <div>{t('auth.mobileIdYourControlCode')}</div>
                            <div className="text-2xl font-medium">{controlCode}</div>
                        </div>

                        <div className="flex flex-col justify-center gap-2 p-4 text-center text-primary-500">
                            <SpinnerIcon className="size-8 animate-spin" />
                            <Countdown
                                duration={appParameters.mobileId.waitDuration}
                                onTimeElapsed={handleTimeElapsed}
                            />
                        </div>

                        <div className="text-center text-neutral-500">{t('auth.mobileIdInstructions')}</div>
                    </div>

                    <Button variant="outlined" color="neutral" onClick={handleCancelClick}>
                        {t('general.cancel')}
                    </Button>
                </div>
            )}
        </>
    );
}
